export type ViewBox = {
  minX: number;
  minY: number;
  width: number;
  height: number;
};

export type BBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

// Agent-authored figures aim for an 8px inner margin (see the custom-html
// skill instructions); repaired viewBoxes use the same breathing room.
const EXPAND_PADDING_PX = 8;

// getBBox returns fractional glyph metrics, so flush-to-edge content can
// measure a fraction outside the box (e.g. x = -0.2) without being clipped
// in any visible way. Only treat an edge as overflowing past this.
const OVERFLOW_TOLERANCE_PX = 0.5;

export function parseViewBox(raw: string | null | undefined): ViewBox | null {
  if (!raw) return null;
  const parts = raw
    .trim()
    .split(/[\s,]+/)
    .map(Number);
  if (parts.length !== 4 || parts.some((value) => !Number.isFinite(value))) {
    return null;
  }
  const [minX, minY, width, height] = parts as [number, number, number, number];
  if (width <= 0 || height <= 0) return null;
  return { minX, minY, width, height };
}

/**
 * Expand `declared` on each side that `bbox` actually overflows, adding
 * padding to the overflowing sides only. Sides the content merely comes close
 * to are left alone — full-bleed backgrounds and bars anchored at x=0 must
 * render exactly as authored, not gain a margin. Only ever grows the box.
 * Returns null when no side overflows.
 */
export function computeExpandedViewBox(
  declared: ViewBox,
  bbox: BBox,
  padding: number = EXPAND_PADDING_PX,
): ViewBox | null {
  const declaredMaxX = declared.minX + declared.width;
  const declaredMaxY = declared.minY + declared.height;
  const bboxMaxX = bbox.x + bbox.width;
  const bboxMaxY = bbox.y + bbox.height;

  const minX =
    bbox.x < declared.minX - OVERFLOW_TOLERANCE_PX
      ? bbox.x - padding
      : declared.minX;
  const minY =
    bbox.y < declared.minY - OVERFLOW_TOLERANCE_PX
      ? bbox.y - padding
      : declared.minY;
  const maxX =
    bboxMaxX > declaredMaxX + OVERFLOW_TOLERANCE_PX
      ? bboxMaxX + padding
      : declaredMaxX;
  const maxY =
    bboxMaxY > declaredMaxY + OVERFLOW_TOLERANCE_PX
      ? bboxMaxY + padding
      : declaredMaxY;

  if (
    minX === declared.minX &&
    minY === declared.minY &&
    maxX === declaredMaxX &&
    maxY === declaredMaxY
  ) {
    return null;
  }
  return { minX, minY, width: maxX - minX, height: maxY - minY };
}
